import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QrCode, Copy, CheckCircle, X, ArrowsLeftRight, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatBRL, formatDate, recurrenceLabel } from "@/lib/format";

/**
 * PlanChangeDialog — lets the client pick a new plan (admin-marked client_switchable)
 * for a given account (main or owned subaccount). Creates a plan-change PIX request.
 * The plan is ONLY applied after the PIX is confirmed by the webhook.
 */
export default function PlanChangeDialog({ open, onOpenChange, account, onApplied }) {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [chosenPlan, setChosenPlan] = useState(null);
  const [request, setRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!open) {
      setPlans([]); setChosenPlan(null); setRequest(null);
      return;
    }
    setLoadingPlans(true);
    api.get("/client/plans/available")
      .then(({ data }) => {
        // Hide plan that is exactly the current one (same id+recurrence)
        const filtered = data.filter((p) => !(p.id === account?.plan_id && p.recurrence === account?.recurrence));
        setPlans(filtered);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Erro ao carregar planos"))
      .finally(() => setLoadingPlans(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.id]);

  // Poll PIX status while request open
  useEffect(() => {
    if (!request?.payment_id || !open) return;
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/client/payments/pix/${request.payment_id}`);
        if (data.status && data.status !== request.status) {
          setRequest((p) => ({ ...p, status: data.status }));
          if (data.status === "approved") {
            toast.success("Troca de plano aprovada! 🎉");
            clearInterval(t); setPolling(false);
            setTimeout(() => { onOpenChange(false); onApplied?.(); }, 1500);
          } else if (["cancelled", "rejected"].includes(data.status)) {
            toast.error("Pagamento não aprovado.");
            clearInterval(t); setPolling(false);
          }
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => { clearInterval(t); setPolling(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.payment_id, open]);

  const submitChange = async () => {
    if (!chosenPlan || !account) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/client/plan-change", {
        user_id: account.id,
        new_plan_id: chosenPlan.id,
      });
      setRequest(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao solicitar troca");
    } finally { setSubmitting(false); }
  };

  const copyCode = async () => {
    if (!request?.qr_code) return;
    try {
      await navigator.clipboard.writeText(request.qr_code);
      toast.success("Código PIX copiado!");
    } catch { toast.error("Não foi possível copiar."); }
  };

  // Step rendering
  // Step 1: pick plan
  // Step 2: review
  // Step 3: pay (PIX)

  const step = request ? 3 : chosenPlan ? 2 : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsLeftRight size={20} weight="duotone" className="text-blue-600"/>
            Trocar plano — {account?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Current plan summary */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3 text-sm" data-testid="pc-current">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 font-semibold">Plano atual</div>
          <div className="flex items-center justify-between mt-1">
            <div className="font-medium">{recurrenceLabel[account?.recurrence] || account?.recurrence}</div>
            <div className="text-zinc-700 tab-nums">{formatBRL(account?.plan_value)}</div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Vence em {formatDate(account?.current_due_date)}</div>
        </div>

        {/* Step 1: pick plan */}
        {step === 1 && (
          <div className="space-y-2 py-2" data-testid="pc-step-pick">
            <div className="text-xs text-zinc-600 mb-1">Escolha o novo plano:</div>
            {loadingPlans ? (
              <div className="text-center py-6 text-zinc-500 text-sm">Carregando planos disponíveis...</div>
            ) : plans.length === 0 ? (
              <div className="text-center py-6 text-zinc-500 text-sm border border-dashed border-zinc-200 rounded-xl">
                Nenhum plano disponível para troca no momento.
              </div>
            ) : (
              <ul className="space-y-2">
                {plans.map((p) => (
                  <li key={`${p.id}-${p.recurrence}`}>
                    <button
                      onClick={() => setChosenPlan(p)}
                      data-testid={`pc-plan-${p.id}`}
                      className="w-full text-left rounded-xl border border-zinc-200 hover:border-blue-400 hover:bg-blue-50/40 p-3 transition-all flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-[11px] text-zinc-500">{recurrenceLabel[p.recurrence] || p.recurrence}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-base tab-nums">{formatBRL(p.effective_amount)}</div>
                        {p.discount_pct > 0 && (
                          <div className="text-[10px] text-emerald-700">-{p.discount_pct}% · base {formatBRL(p.base_value)}</div>
                        )}
                      </div>
                      <CaretRight size={16} className="text-zinc-400 flex-shrink-0"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Step 2: review */}
        {step === 2 && chosenPlan && (
          <div className="space-y-3 py-2" data-testid="pc-step-review">
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3">
              <div className="text-[10px] tracking-widest uppercase text-blue-700 font-semibold">Novo plano selecionado</div>
              <div className="mt-1 flex items-center justify-between">
                <div>
                  <div className="font-display text-base font-semibold">{chosenPlan.name}</div>
                  <div className="text-[11px] text-zinc-600">{recurrenceLabel[chosenPlan.recurrence] || chosenPlan.recurrence}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-bold tab-nums">{formatBRL(chosenPlan.effective_amount)}</div>
                  {chosenPlan.discount_pct > 0 && (
                    <div className="text-[10px] text-emerald-700">-{chosenPlan.discount_pct}% · base {formatBRL(chosenPlan.base_value)}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 space-y-1.5">
              <div className="font-semibold">Como funciona:</div>
              <div>1. Você gera um PIX agora pelo valor do novo plano.</div>
              <div>2. Enquanto o pagamento não é confirmado, seu plano atual continua ativo.</div>
              <div>3. Assim que o Mercado Pago aprovar o pagamento, o plano é trocado automaticamente.</div>
            </div>

            <Button
              onClick={submitChange}
              disabled={submitting}
              data-testid="pc-confirm"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11"
            >
              {submitting ? "Gerando PIX..." : "Confirmar e pagar via PIX"}
            </Button>
            <Button
              onClick={() => setChosenPlan(null)}
              variant="outline"
              className="w-full"
              data-testid="pc-back"
            >
              Voltar
            </Button>
          </div>
        )}

        {/* Step 3: PIX QR + polling */}
        {step === 3 && request && (
          <div className="space-y-3 py-2" data-testid="pc-step-pay">
            <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-center">
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 font-semibold">Valor da troca</div>
              <div className="metric-num text-2xl mt-1">{formatBRL(request.amount)}</div>
              <div className="text-[11px] text-zinc-600 mt-1">
                {request.new_plan?.name} · {recurrenceLabel[request.new_plan?.recurrence] || request.new_plan?.recurrence}
              </div>
              {request.projected_new_due_date && (
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Após aprovação, vencerá em {formatDate(request.projected_new_due_date)}
                </div>
              )}
            </div>
            {request.qr_code_base64 ? (
              <>
                <div className="rounded-xl border border-zinc-200 bg-white p-3 grid place-items-center">
                  <img
                    src={`data:image/png;base64,${request.qr_code_base64}`}
                    alt="QR Code PIX"
                    data-testid="pc-pix-qr"
                    className="w-52 h-52 object-contain"
                  />
                </div>
                <button
                  onClick={copyCode}
                  data-testid="pc-pix-copy"
                  className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2"
                >
                  <Copy size={16}/> Copiar código PIX
                </button>
              </>
            ) : (
              <div className="text-center py-4 text-rose-600 text-sm">
                Não foi possível gerar o PIX. Tente novamente.
              </div>
            )}
            {polling && request.status === "pending" && (
              <div className="flex items-center justify-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                Aguardando confirmação do Mercado Pago...
              </div>
            )}
            {request.status === "approved" && (
              <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-3 font-medium">
                <CheckCircle weight="fill" size={18}/> Plano trocado com sucesso!
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pc-close">
            <X size={14} className="mr-1.5"/> Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
